import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Divider,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import ListAltIcon from "@material-ui/icons/ListAlt";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import RestaurantMenuIcon from "@material-ui/icons/RestaurantMenu";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import DashboardIcon from '@material-ui/icons/Dashboard';
import MenuIcon from "@material-ui/icons/Menu";
import PermContactCalendarIcon from '@material-ui/icons/PermContactCalendar';
import SmartphoneIcon from '@material-ui/icons/Smartphone';

const useStyles = makeStyles((theme) => ({
  appBar: {
    background: "rgba(30, 58, 138, 0.9)",
    backdropFilter: "blur(8px)",
    boxShadow: "0 4px 30px rgba(0,0,0,0.1)",
    borderBottom: "1px solid rgba(255,255,255,0.15)",
    zIndex: 1300,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 70,
    padding: theme.spacing(0, 2),
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    backgroundColor: "rgba(255,255,255,0.12)",
    padding: theme.spacing(0.5, 1.2),
    borderRadius: 12,
    backdropFilter: "blur(6px)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
      transform: "scale(1.02)",
    },
  },
  logo: {
    height: 42,
    width: "auto",
    borderRadius: 8,
    filter: "brightness(1.5) contrast(1.2) drop-shadow(0 0 4px rgba(0,0,0,0.4))",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      filter: "brightness(1.8) contrast(1.3) drop-shadow(0 0 6px rgba(0,0,0,0.5))",
    },
  },
  title: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: "#fff",
    letterSpacing: "0.4px",
  },
  buttonGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  baseButton: {
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    padding: "8px 18px",
    borderRadius: 10,
    transition: "all 0.25s ease",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
    },
  },
  pedidos: {
    backgroundColor: "#f97316",
    "&:hover": { backgroundColor: "#ea580c" },
  },
  dashboard: {
    backgroundColor: "#14b8a6", // teal-500
    "&:hover": { backgroundColor: "#0d9488" }, // teal-600
  },

  status: {
    backgroundColor: "#1ea3f0ff", // teal-500
    "&:hover": { backgroundColor: "#1978afff" }, // teal-600
  },

  contacts: {
    backgroundColor: "#fa5e5eff", // teal-500
    "&:hover": { backgroundColor: "#c54c4cff" }, // teal-600
  },
  whats: {
    backgroundColor: "#16a34a",
    "&:hover": { backgroundColor: "#11863cff" },
  },
  cardapio: {
    backgroundColor: "#3b82f6",
    "&:hover": { backgroundColor: "#1d4ed8" },
  },
  produtos: {
    backgroundColor: "#8b5cf6",
    "&:hover": { backgroundColor: "#6d28d9" },
  },
  sair: {
    backgroundColor: "#ef4444",
    "&:hover": { backgroundColor: "#b91c1c" },
  },
  menuButton: {
    display: "none",
    color: "#fff",
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  drawerPaper: {
    backgroundColor: "#1e3a8a",
    color: "#fff",
    padding: theme.spacing(2, 1),
    width: 240,
  },
  drawerIcon: {
    width: 42,
    height: 42,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1.25),
    flexShrink: 0,
  },
  drawerHeader: {
    textAlign: "center",
    marginBottom: theme.spacing(1),
    fontWeight: 700,
    color: "#fff",
  },
  drawerItem: {
    borderRadius: 8,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.1)",
    },
  },
  drawerItemText: {
    marginLeft: 0,
    fontWeight: 600,
    color: "#fff",
  },
}));

export default function Header({ showButtons = true }) {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.reload();
  };

  const goTo = (route) => {
    history.push(route);
    setDrawerOpen(false);
  };

  const buttons = (
    <>
      <Button
        className={`${classes.baseButton} ${classes.pedidos}`}
        startIcon={<ListAltIcon />}
        onClick={() => goTo("/chat")}
      >
        Pedidos
      </Button>
      <Button
        className={`${classes.baseButton} ${classes.cardapio}`}
        startIcon={<RestaurantMenuIcon />}
        onClick={() => goTo("/cardapio")}
      >
        Cardápio
      </Button>
      <Button
        className={`${classes.baseButton} ${classes.produtos}`}
        startIcon={<ShoppingCartIcon />}
        onClick={() => goTo("/produtos")}
      >
        Produtos
      </Button>
      <Button
        className={`${classes.baseButton} ${classes.dashboard}`}
        startIcon={<DashboardIcon />}
        onClick={() => goTo("/dashboard")}
      >
        Dashboard
      </Button>

      <Button
        className={`${classes.baseButton} ${classes.status}`}
        startIcon={<SmartphoneIcon />}
        onClick={() => goTo("/status")}
      >
        Stories
      </Button>

      <Button
        className={`${classes.baseButton} ${classes.contacts}`}
        startIcon={<PermContactCalendarIcon />}
        onClick={() => goTo("/contacts")}
      >
        Contatos
      </Button>

      <Button
        className={`${classes.baseButton} ${classes.whats}`}
        startIcon={<WhatsAppIcon />}
        onClick={() => goTo("/whatsapp")}
      >
        WhatsApp
      </Button>
      <Button
        className={`${classes.baseButton} ${classes.sair}`}
        startIcon={<ExitToAppIcon />}
        onClick={handleLogout}
      >
        Sair
      </Button>
    </>
  );

  return (
    <>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Box
            className={classes.logoContainer}
            onClick={() => history.push("/chat")}
          >
            <img
              src="/whatsdelivery2.png"
              alt="WhatsDelivery"
              className={classes.logo}
            />
            <Typography className={classes.title}>WhatsDelivery</Typography>
          </Box>
          {showButtons && (
            <>
              <Box className={classes.buttonGroup}>{buttons}</Box>
              {isMobile && (
                <IconButton
                  edge="end"
                  className={classes.menuButton}
                  onClick={() => setDrawerOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>
      {/* Drawer (menu mobile) */}
      {showButtons && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          classes={{ paper: classes.drawerPaper }}
        >
          <Typography variant="h6" className={classes.drawerHeader}>
            Menu
          </Typography>
          <Divider style={{ backgroundColor: "rgba(255,255,255,0.2)", marginBottom: 8 }} />
          <List>
            {[
              { text: "Pedidos", icon: <ListAltIcon />, route: "/chat", color: "#f97316" },
              { text: "Conectar WhatsApp", icon: <WhatsAppIcon />, route: "/whatsapp", color: "#16a34a" },
              { text: "Cardápio", icon: <RestaurantMenuIcon />, route: "/cardapio", color: "#3b82f6" },
              { text: "Produtos", icon: <ShoppingCartIcon />, route: "/produtos", color: "#8b5cf6" },
              { text: "Dashboard", icon: <DashboardIcon />, route: "/dashboard", color: "#14b8a6" },
              { text: "Stories", icon: <SmartphoneIcon />, route: "/status", color: "#1ea3f0" },
              { text: "Contatos", icon: <PermContactCalendarIcon />, route: "/contacts", color: "#fa5e5e" },
            ].map((item, i) => (
              <ListItem
                button
                key={i}
                className={classes.drawerItem}
                onClick={() => goTo(item.route)}
                style={{ padding: "10px 12px", marginBottom: 8 }}
              >
                <div
                  className={classes.drawerIcon}
                  style={{ background: item.color }}
                >
                  {React.cloneElement(item.icon, { style: { color: "#fff" } })}
                </div>
                <ListItemText
                  primary={item.text}
                  classes={{ primary: classes.drawerItemText }}
                />
              </ListItem>
            ))}
          </List>
          <Divider style={{ backgroundColor: "rgba(255,255,255,0.2)", margin: "8px 0" }} />
          <ListItem button className={classes.drawerItem} onClick={handleLogout}>
            <ExitToAppIcon />
            <ListItemText
              primary="Sair"
              style={{ marginLeft: 10, color: "#fca5a5", fontWeight: 500 }}
            />
          </ListItem>
        </Drawer>
      )}
    </>
  );
}
